import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Trophy, Plus, Trash2, Gift, Crown, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ROLE = {
  beauty_creator: { label: "Beauty Creator", Icon: Crown, cls: "bg-showty-red text-white" },
  affiliator: { label: "Affiliator", Icon: Video, cls: "bg-showty-navy text-white" },
};

export default function AwardsPanel({ editable = false }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/awards").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { try { await api.delete(`/awards/${id}`); load(); } catch (e) { toast.error("Gagal menghapus"); } };

  return (
    <div data-testid="awards-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><Trophy className="w-5 h-5 text-showty-gold" /> Awards Showtynion</h3>
          <p className="text-sm text-muted-foreground">{editable ? "Umumkan pemenang KOL & Affiliator beserta hadiah/benefit-nya." : "Selamat untuk para pemenang! Intip hadiah & benefit-nya di bawah."}</p>
        </div>
        {editable && <AwardDialog onDone={load} />}
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="awards-empty">
          <Trophy className="w-6 h-6 mx-auto mb-2 text-showty-gold/60" /> Belum ada pengumuman award
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="awards-list">
          {items.map((a) => {
            const r = ROLE[a.role] || ROLE.beauty_creator;
            const RIcon = r.Icon;
            return (
              <div key={a.id} className="rounded-2xl border border-showty-gold/40 bg-gradient-to-b from-white to-showty-cream overflow-hidden flex flex-col" data-testid={`award-${a.id}`}>
                {a.image_url && <div className="h-36 overflow-hidden"><img src={a.image_url} alt={a.winner_name} className="w-full h-full object-cover" /></div>}
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[10px] uppercase tracking-wide font-semibold rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${r.cls}`}><RIcon className="w-3 h-3" /> {r.label}</span>
                    {editable && <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive" data-testid={`award-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-showty-gold shrink-0" />
                    <h4 className="font-display text-xl text-showty-navy leading-tight">{a.winner_name}</h4>
                  </div>
                  {a.period && <p className="text-xs text-muted-foreground mt-0.5">{a.period}</p>}
                  {a.prize && (
                    <div className="mt-3 rounded-xl bg-showty-pink/30 border border-showty-red/20 px-3 py-2 flex items-start gap-2">
                      <Gift className="w-4 h-4 text-showty-red shrink-0 mt-0.5" />
                      <p className="text-sm font-medium text-showty-navy">{a.prize}</p>
                    </div>
                  )}
                  {a.description && <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap flex-1">{a.description}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const AwardDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const empty = { winner_name: "", role: "beauty_creator", prize: "", description: "", period: "", image_url: "" };
  const [f, setF] = useState(empty);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.winner_name.trim()) return toast.error("Nama pemenang wajib diisi");
    try { await api.post("/awards", f); toast.success("Award diumumkan"); setOpen(false); setF(empty); onDone?.(); }
    catch (e) { toast.error("Gagal menyimpan award"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="award-add-btn"><Plus className="w-4 h-4 mr-1" /> Umumkan Pemenang</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="award-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">Pengumuman Award</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Nama Pemenang</Label><Input className="mt-1" value={f.winner_name} onChange={(e) => set("winner_name", e.target.value)} data-testid="award-winner" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kategori</Label>
              <Select value={f.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="mt-1" data-testid="award-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="beauty_creator">Beauty Creator (KOL)</SelectItem>
                  <SelectItem value="affiliator">Affiliator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Periode (opsional)</Label><Input className="mt-1" placeholder="Juni 2026" value={f.period} onChange={(e) => set("period", e.target.value)} data-testid="award-period" /></div>
          </div>
          <div><Label>Hadiah / Benefit</Label><Input className="mt-1" placeholder="Voucher Rp 1jt + PR package eksklusif" value={f.prize} onChange={(e) => set("prize", e.target.value)} data-testid="award-prize" /></div>
          <div><Label>Deskripsi</Label><Textarea rows={3} className="mt-1" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="award-desc" /></div>
          <div><Label>URL Foto (opsional)</Label><Input className="mt-1" value={f.image_url} onChange={(e) => set("image_url", e.target.value)} data-testid="award-image" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="award-save">Publikasikan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
